import { FC, useEffect, useState } from 'react'
import { Wizard } from 'react-use-wizard'
import { useMutation, useQuery } from '@apollo/client'

import { useAppDispatch, useAppSelector } from 'hooks/store'
import CustomModal from 'components/organisms/CustomModal'
import StepStart from 'components/organisms/DocumentAddModalSteps/StepStart'
import StepTakePhoto from 'components/organisms/DocumentAddModalSteps/StepTakePhoto'
import StepSubmitFiles from 'components/organisms/DocumentAddModalSteps/StepSubmitFiles'
import StepEditFile from 'components/organisms/DocumentAddModalSteps/StepEditFile'
import StepPreviewPhoto from 'components/organisms/DocumentAddModalSteps/StepPreviewPhoto'
import StepFinish from 'components/organisms/DocumentAddModalSteps/StepFinish'
import {
  fileFromBase64String,
  getFileNameExtension,
  getMimeType,
  renameFile
} from 'share/utils/fileUtils'
import {
  DocumentUploadResponse,
  DocumentUploadVariables,
  DOCUMENT_UPLOAD
} from 'share/mutations/documents'
import {
  GET_DOCUMENT_DASHBOARD,
  GET_CHILD_FOLDERS_FOR_PARENT_FOLDER_QUERY,
  GET_DOCUMENTS_CHILD_LEAF_CATEGORY_QUERY,
  GET_DEFAULT_DOCUMENT_CATEGORYID,
  GetDefaultDocumentCategoryIdResponse
} from 'share/queries/documents'
import StepViewFile from 'components/organisms/DocumentAddModalSteps/StepViewFile'
import {
  setSelectedDocument,
  setDocumentUploadList,
  getEmptyUploadedDoc,
  UploadedDoc
} from 'reducers/document/documentProcessingSlice'

export interface DocumentsAddReplaceModalProps {
  businessObjectId: number | null
  businessObjectType: string
  onHide: () => void
  replacedDocumentId: number | null
  show: boolean
  title: string
  folderName: string
  folderDescription: string
  isAiCase: boolean
}

const ERROR_SCAN =
  '{fileName} may be corrupt and could not be submitted. Please try a different file.'

const DocumentsAddReplaceModal: FC<DocumentsAddReplaceModalProps> = ({
  businessObjectId,
  businessObjectType,
  onHide,
  replacedDocumentId,
  show,
  title,
  folderName,
  folderDescription,
  isAiCase
}) => {
  const dispatch = useAppDispatch()
  const documentProcessing = useAppSelector((state) => state.documentProcessing)
  const activeCase = useAppSelector((state) => state.activeCase)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('  ')
  const [uploading, setUploading] = useState(false)
  const [photo, setPhoto] = useState('')
  const [errorUploading, setErrorUploading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [files, setFiles] = useState(new Array<File>())
  const [fileToEdit, setFileToEdit] = useState<File>()
  const [indexFileToEdit, setIndexFileToEdit] = useState<number>(0)

  // Fetch default document category id to use for uploads
  const { data: defaultCategoryData } =
    useQuery<GetDefaultDocumentCategoryIdResponse>(
      GET_DEFAULT_DOCUMENT_CATEGORYID
    )

  const [uploadFileMutation] =
    useMutation<DocumentUploadResponse, DocumentUploadVariables>(
      DOCUMENT_UPLOAD
    )

  useEffect(() => {
    setName(folderName)
    setDescription(folderDescription)
  }, [folderName, folderDescription])

  const setCorrectErrorMessage = (errorFileNames?: string): void => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    setErrorMessage(
      ERROR_SCAN.replace('{fileName}', errorFileNames ?? 'Your document')
    )
  }

  const onStartedUploading = async () => {
    const errorFiles: string[] = []
    console.log('onStartedUploading caseId', activeCase)
    setUploading(true)
    setErrorUploading(false)
    try {
      Promise.all(
        files.map((file) => {
          return uploadFileMutation({
            variables: {
              file,
              fileMetadata: {
                caseId: activeCase.matter?.id ?? 0,
                categoryId:
                  documentProcessing.selectedDocumentCategoryId ??
                  defaultCategoryData?.documents.defaultCategoryDocumentFolder
                    .categoryId ??
                  0,
                documentType: file.type || getMimeType(file.name),
                replacedDocumentId,
                businessObjectId,
                businessObjectType
              }
            },
            refetchQueries: [
              {
                query: GET_DOCUMENT_DASHBOARD,
                variables: {
                  caseId: activeCase.matter?.id ?? 0
                }
              },
              {
                query: GET_CHILD_FOLDERS_FOR_PARENT_FOLDER_QUERY,
                variables: {
                  categoryId:
                    documentProcessing.selectedDocumentCategoryId ?? 0,
                  caseId: activeCase.matter?.id ?? 0
                }
              },

              {
                query: GET_DOCUMENTS_CHILD_LEAF_CATEGORY_QUERY,
                variables: {
                  categoryId:
                    documentProcessing.selectedDocumentCategoryId ?? 0,
                  caseId: activeCase.matter?.id ?? 0
                }
              }
            ]
          }).catch((error) => {
            errorFiles.push(file.name)
            console.log('MUTATION ERROR: ', error.message)
            return error
          })
        })
      )
        .then((results) => {
          if (errorFiles.length > 0) {
            setCorrectErrorMessage(errorFiles.join(', '))
            setErrorUploading(true)
          } else {
            const newDoc = Object.assign(
              {},
              documentProcessing.selectedDocument
            )

            const uploadedDocList: UploadedDoc[] = []
            results.map((r) => {
              const uploadDoc = getEmptyUploadedDoc()
              uploadDoc.id = r.data.documents.uploadFile.uploadedDocument.id
              uploadDoc.fileName =
                r.data.documents.uploadFile.uploadedDocument.name
              uploadedDocList.push(uploadDoc)
            })
            dispatch(setDocumentUploadList(uploadedDocList))
            newDoc.id = results[0].data.documents.uploadFile.uploadedDocument.id
            newDoc.name =
              results[0].data.documents.uploadFile.uploadedDocument.name
            newDoc.status =
              results[0].data.documents.uploadFile.uploadedDocument.status
            newDoc.uploaded =
              results[0].data.documents.uploadFile.uploadedDocument.uploaded
            newDoc.comments = []

            dispatch(setSelectedDocument(newDoc))
          }
        })
        .catch((error) => {
          console.log('PROMISE ALL ERROR: ', error)
          setCorrectErrorMessage()
          setErrorUploading(true)
        })
        .finally(() => setUploading(false))
    } catch (error) {
      console.log(error)
      setCorrectErrorMessage()
      setErrorUploading(true)
    }
  }

  const onSelectedFile = (selectedFile: File) => {
    const newFiles = [...files, selectedFile]
    setFiles(newFiles)
    setFileToEdit(selectedFile)
    setIndexFileToEdit(newFiles.length - 1)
  }

  const onPhotoTaken = (image: string) => {
    setPhoto(image)
  }

  const onPhotoSelected = (image: string) => {
    const date = new Date()
    const filename = `${
      date.toISOString().split('T')[0]
    }_${date.getMilliseconds()}.png`
    const photoFile = fileFromBase64String(image, filename, 'image/png')
    onSelectedFile(photoFile)
  }

  const onEditFileStarted = (index?: number) => {
    if (index !== undefined) {
      setFileToEdit(files[index])
      setIndexFileToEdit(index)
    }
  }

  const onEditFileFinished = (newFileName: string, index: number) => {
    const newFiles = [...files]
    const [, extension] = getFileNameExtension(newFiles[index].name)
    const newFile = renameFile(newFiles[index], `${newFileName}.${extension}`)
    newFiles.splice(index, 1, newFile)
    setFiles(newFiles)
  }

  const onDeleteFile = (index?: number) => {
    if (index !== undefined) {
      const newFiles = [...files]
      newFiles.splice(index, 1)
      setFiles(newFiles)
    }
  }

  const onCancel = () => {
    setFiles(new Array<File>())
    setErrorUploading(false)
    setUploading(false)
    setPhoto('')
    onHide()
  }

  return (
    <CustomModal
      showFooter
      messageText={errorMessage}
      onHide={onCancel}
      show={show}
      showClose={!uploading}
      showMessage={errorUploading}
      title={title}
    >
      <Wizard>
        <StepStart
          subcategoryName={name}
          subcategoryDescription={description}
          files={files}
          onSelectedFile={onSelectedFile}
          onCancel={onCancel}
          showTotalSizeMessage={title !== 'Replace Document' || isAiCase}
          isAiCase={isAiCase}
        />
        <StepTakePhoto onPhotoTaken={onPhotoTaken} />
        <StepPreviewPhoto photo={photo} onPhotoSelected={onPhotoSelected} />
        <StepEditFile
          subcategoryName={name}
          subcategoryDescription={description}
          file={fileToEdit}
          fileIndex={indexFileToEdit}
          onEditFinished={onEditFileFinished}
          onDeleteFile={onDeleteFile}
        />
        <StepSubmitFiles
          document={documentProcessing}
          activeCase={activeCase}
          subcategoryName={name}
          subcategoryDescription={description}
          files={files}
          onStartedUploading={onStartedUploading}
          onCancel={onCancel}
          onEdit={onEditFileStarted}
          onDeleteFile={onDeleteFile}
          showAddMoreDocs={title !== 'Replace Document' || isAiCase}
        />
        <StepViewFile file={fileToEdit} />
        <StepFinish
          uploading={uploading}
          onFinish={onCancel}
          error={errorUploading}
        />
      </Wizard>
    </CustomModal>
  )
}

export default DocumentsAddReplaceModal
